/**
 * 新建导演项目对话框（参考电商复刻视觉语言）
 *
 * 两栏布局：
 *   左栏「基础信息」：项目名 / 简介 / 默认分镜模式
 *   右栏「生成配置」：视频模型 / 画面比例 / 清晰度 / 图片模型
 *
 * 关键设计：
 * - 视频模型从 /director/projects/video-models 拉取（已按 img2video 能力过滤）
 * - 选择模型后，画面比例 / 清晰度按该模型 img2video 渠道 provider 声明动态过滤
 */

import { useEffect, useMemo, useState } from 'react';
import { X, Loader2, Sparkles, Film, Layers, Info } from 'lucide-react';
import toast from '../../../components/Toast';
import {
  directorApi,
  DirectorApiError,
  type DirectorProjectMode,
  type DirectorCompatibleVideoModel,
} from '../services/directorApi';
import { api as imageApi, type ModelConfig as ImageModelConfig } from '../../../services/api';

interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (projectId: string) => void;
}

type AspectValue = '16:9' | '9:16' | '1:1';
type ResolutionValue = '480p' | '720p' | '1080p';

const ASPECT_OPTIONS: Array<{ value: AspectValue; label: string; hint: string }> = [
  { value: '16:9', label: '16:9', hint: '横屏 / 长视频' },
  { value: '9:16', label: '9:16', hint: '抖音 / 小红书' },
  { value: '1:1', label: '1:1', hint: '社交方屏' },
];

const RESOLUTION_OPTIONS: Array<{ value: ResolutionValue; label: string; hint: string }> = [
  { value: '480p', label: '480p', hint: '快速预览' },
  { value: '720p', label: '720p', hint: '推荐 · 平衡' },
  { value: '1080p', label: '1080p', hint: '高清成片' },
];

export function NewProjectDialog({ open, onClose, onCreated }: NewProjectDialogProps) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [mode, setMode] = useState<DirectorProjectMode>('video_per_shot');
  const [aspectRatio, setAspectRatio] = useState<AspectValue>('16:9');
  const [resolution, setResolution] = useState<ResolutionValue>('720p');
  const [videoModelId, setVideoModelId] = useState('');
  const [models, setModels] = useState<DirectorCompatibleVideoModel[]>([]);
  const [loadingModels, setLoadingModels] = useState(false);
  const [imageModelId, setImageModelId] = useState('');
  const [imageModels, setImageModels] = useState<ImageModelConfig[]>([]);
  const [loadingImageModels, setLoadingImageModels] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modeSwitch, setModeSwitch] = useState({
    keyframeEnabled: true,
    frameSequenceEnabled: true,
    trinityCardsEnabled: true,
  });

  useEffect(() => {
    if (!open) return;
    setLoadingModels(true);
    directorApi
      .listCompatibleVideoModels()
      .then((list) => {
        setModels(list);
        if (list.length > 0 && !videoModelId) setVideoModelId(list[0].modelId);
      })
      .catch((err) => {
        console.error(err);
        toast.error('视频模型加载失败');
      })
      .finally(() => setLoadingModels(false));

    setLoadingImageModels(true);
    imageApi
      .getModels()
      .then((d) => {
        setImageModels(d.data);
        if (d.data.length > 0 && !imageModelId) setImageModelId(d.data[0].id);
      })
      .catch((err) => {
        console.error(err);
        toast.error('图片模型加载失败');
      })
      .finally(() => setLoadingImageModels(false));

    directorApi
      .getPublicSettings()
      .then((res) => {
        const switches = {
          keyframeEnabled: res.keyframeEnabled,
          frameSequenceEnabled: res.frameSequenceEnabled,
          trinityCardsEnabled: res.trinityCardsEnabled,
        };
        setModeSwitch(switches);
        // 若当前默认模式被禁用，自动切换到第一个可用模式
        const currentEnabled =
          (mode === 'video_per_shot' && switches.keyframeEnabled) ||
          (mode === 'image_sequence' && switches.frameSequenceEnabled) ||
          (mode === 'trinity_cards' && switches.trinityCardsEnabled);
        if (!currentEnabled) {
          if (switches.keyframeEnabled) setMode('video_per_shot');
          else if (switches.frameSequenceEnabled) setMode('image_sequence');
          else if (switches.trinityCardsEnabled) setMode('trinity_cards');
        }
      })
      .catch(() => undefined);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // 当前选中的视频模型详情（提供联动信息）
  const selectedModel = useMemo(
    () => models.find((m) => m.modelId === videoModelId) ?? null,
    [models, videoModelId]
  );

  // 模型变更时，若当前 aspect/resolution 不在该模型可选范围内，自动回退到第一个可选项
  useEffect(() => {
    if (!selectedModel) return;
    if (
      selectedModel.availableAspectRatios.length > 0 &&
      !selectedModel.availableAspectRatios.includes(aspectRatio)
    ) {
      const fallback = (selectedModel.availableAspectRatios.includes('16:9')
        ? '16:9'
        : selectedModel.availableAspectRatios[0]) as AspectValue;
      setAspectRatio(fallback);
    }
    if (
      selectedModel.availableResolutions.length > 0 &&
      !selectedModel.availableResolutions.includes(resolution)
    ) {
      const fallback = (selectedModel.availableResolutions.includes('720p')
        ? '720p'
        : selectedModel.availableResolutions[0]) as ResolutionValue;
      setResolution(fallback);
    }
  }, [selectedModel]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('项目名不能为空');
      return;
    }
    if (!videoModelId) {
      toast.error('请选择视频模型');
      return;
    }
    setSubmitting(true);
    try {
      const project = await directorApi.createProject({
        name: name.trim(),
        description: description.trim() || undefined,
        aspectRatio,
        resolution,
        videoModelId,
        imageModelId: imageModelId || undefined,
        mode,
      });
      toast.success('项目已创建');
      onCreated(project.id);
    } catch (err) {
      toast.error(err instanceof DirectorApiError ? err.message : '创建失败');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  // 当前模型支持的比例 / 分辨率（兜底：模型缺失时显示全部三档）
  const aspectOptionsForModel = selectedModel
    ? ASPECT_OPTIONS.filter((o) => selectedModel.availableAspectRatios.includes(o.value))
    : ASPECT_OPTIONS;
  const resolutionOptionsForModel = selectedModel
    ? RESOLUTION_OPTIONS.filter((o) => selectedModel.availableResolutions.includes(o.value))
    : RESOLUTION_OPTIONS;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 顶部深色 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white sm:p-6">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                新建导演项目
              </div>
              <h2 className="text-lg font-semibold leading-tight">建一部你自己的短剧</h2>
              <p className="mt-1.5 text-xs leading-5 text-slate-300">
                选择视频模型，整个项目内所有分镜会使用同一模型，避免跨编码转码
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
              aria-label="关闭"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* 表单内容（两栏） */}
        <div className="flex-1 overflow-y-auto bg-slate-50 p-5 sm:p-6">
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-2 lg:gap-6">
            {/* ─────────────────── 左栏：基础信息 ─────────────────── */}
            <div className="space-y-5">
              <SectionTitle>基础信息</SectionTitle>

              {/* 项目名 */}
              <div>
                <label className="mb-1.5 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  项目名 <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value.slice(0, 60))}
                  placeholder="例如:古风奇遇 第一集"
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400">{name.length}/60</p>
              </div>

              {/* 简介 */}
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  简介（可选）
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, 200))}
                  placeholder="一句话描述这部短剧"
                  rows={3}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
                />
                <p className="mt-1 text-right text-[11px] text-slate-400">
                  {description.length}/200
                </p>
              </div>

              {/* 默认分镜模式 */}
              <div>
                <label className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  默认分镜模式 <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  {modeSwitch.keyframeEnabled && (
                    <button
                      type="button"
                      onClick={() => setMode('video_per_shot')}
                      className={`flex items-start gap-2.5 rounded-2xl border p-3 text-left transition ${
                        mode === 'video_per_shot'
                          ? 'border-sky-500 bg-sky-50 shadow-sm shadow-sky-100'
                          : 'border-slate-200 bg-white hover:border-sky-200'
                      }`}
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-cyan-400 text-white shadow-sm">
                        <Film className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-900">关键帧</div>
                        <div className="mt-0.5 text-[11px] leading-4 text-slate-500">
                          每镜 1 张关键帧 → 1 段视频
                        </div>
                      </div>
                    </button>
                  )}
                  {modeSwitch.frameSequenceEnabled && (
                    <button
                      type="button"
                      onClick={() => setMode('image_sequence')}
                      className={`flex items-start gap-2.5 rounded-2xl border p-3 text-left transition ${
                        mode === 'image_sequence'
                          ? 'border-amber-500 bg-amber-50 shadow-sm shadow-amber-100'
                          : 'border-slate-200 bg-white hover:border-amber-200'
                      }`}
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-400 text-white shadow-sm">
                        <Layers className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                          帧序列
                          <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">
                            Beta
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] leading-4 text-slate-500">
                          每镜 N 张帧图，多参考图 → 1 段视频
                        </div>
                      </div>
                    </button>
                  )}
                  {modeSwitch.trinityCardsEnabled && (
                    <button
                      type="button"
                      onClick={() => setMode('trinity_cards')}
                      className={`flex items-start gap-2.5 rounded-2xl border p-3 text-left transition ${
                        mode === 'trinity_cards'
                          ? 'border-violet-500 bg-violet-50 shadow-sm shadow-violet-100'
                          : 'border-slate-200 bg-white hover:border-violet-200'
                      }`}
                    >
                      <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-sm">
                        <Sparkles className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                          三合一
                          <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-violet-700">
                            New
                          </span>
                        </div>
                        <div className="mt-0.5 text-[11px] leading-4 text-slate-500">
                          角色卡 + 故事卡 + 视频卡 → 1 段视频
                        </div>
                      </div>
                    </button>
                  )}
                </div>
                <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
                  这是新建分镜的默认模式，每个分镜可在工作台里单独切换。
                </p>
              </div>
            </div>

            {/* ─────────────────── 右栏：生成配置 ─────────────────── */}
            <div className="space-y-5">
              <SectionTitle>生成配置</SectionTitle>

              {/* 视频模型 */}
              <div>
                <label className="mb-1.5 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  视频模型 <span className="text-rose-500">*</span>
                </label>
                {loadingModels ? (
                  <div className="rounded-2xl bg-white px-4 py-2.5 text-sm text-slate-500">
                    加载中...
                  </div>
                ) : models.length === 0 ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                    暂无可用模型。导演模式需要支持图生视频的渠道，请联系管理员配置
                  </div>
                ) : (
                  <select
                    value={videoModelId}
                    onChange={(e) => setVideoModelId(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300"
                  >
                    {models.map((m) => (
                      <option key={m.modelId} value={m.modelId}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
                  整个项目所有分镜将使用同一模型。列表只显示具备图生视频能力的模型。
                </p>

                {/* 选中模型的能力提示 */}
                {selectedModel && (
                  <div className="mt-2 space-y-1.5">
                    {selectedModel.description && (
                      <div className="flex items-start gap-1.5 rounded-xl bg-slate-100 px-3 py-2 text-[11px] leading-4 text-slate-600">
                        <Info className="mt-0.5 h-3 w-3 shrink-0 text-slate-400" />
                        <span className="flex-1">{selectedModel.description}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* 画面比例 */}
              <div>
                <label className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  画面比例 <span className="text-rose-500">*</span>
                </label>
                {aspectOptionsForModel.length === 0 ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                    所选模型未声明可用画面比例
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {aspectOptionsForModel.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setAspectRatio(opt.value)}
                        className={`rounded-2xl border p-2.5 text-left transition ${
                          aspectRatio === opt.value
                            ? 'border-sky-500 bg-sky-50 shadow-sm shadow-sky-100'
                            : 'border-slate-200 bg-white hover:border-sky-200'
                        }`}
                      >
                        <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{opt.hint}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* 清晰度 */}
              <div>
                <label className="mb-2 flex items-center gap-1 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  清晰度 <span className="text-rose-500">*</span>
                </label>
                {resolutionOptionsForModel.length === 0 ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                    所选模型未声明可用清晰度
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2">
                    {resolutionOptionsForModel.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setResolution(opt.value)}
                        className={`rounded-2xl border p-2.5 text-left transition ${
                          resolution === opt.value
                            ? 'border-sky-500 bg-sky-50 shadow-sm shadow-sky-100'
                            : 'border-slate-200 bg-white hover:border-sky-200'
                        }`}
                      >
                        <div className="text-sm font-semibold text-slate-900">{opt.label}</div>
                        <div className="mt-0.5 text-[11px] text-slate-500">{opt.hint}</div>
                      </button>
                    ))}
                  </div>
                )}
                <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
                  清晰度会同时影响所有分镜的视频生成与计费倍率。
                </p>
              </div>

              {/* 图片模型 */}
              <div>
                <label className="mb-1.5 block text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
                  图片模型
                </label>
                {loadingImageModels ? (
                  <div className="rounded-2xl bg-white px-4 py-2.5 text-sm text-slate-500">
                    加载中...
                  </div>
                ) : imageModels.length === 0 ? (
                  <div className="rounded-2xl border border-rose-100 bg-rose-50 px-4 py-2.5 text-sm text-rose-600">
                    暂无可用图片模型，请联系管理员配置
                  </div>
                ) : (
                  <select
                    value={imageModelId}
                    onChange={(e) => setImageModelId(e.target.value)}
                    className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-900 outline-none transition focus:border-sky-300"
                  >
                    {imageModels.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                  </select>
                )}
                <p className="mt-1.5 text-[11px] leading-4 text-slate-500">
                  用于生成分镜关键帧、三合一卡、角色定妆照、场景设定图。不选则使用系统默认。
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 底部操作 */}
        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !name.trim() || !videoModelId}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            创建项目
          </button>
        </div>
      </div>
    </div>
  );
}

/** 栏目标题：左侧细色条 + 文字，给两栏一个清晰的视觉锚点 */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-slate-200 pb-2">
      <span className="h-3.5 w-1 rounded-full bg-gradient-to-b from-sky-400 to-sky-600" />
      <span className="text-sm font-semibold text-slate-900">{children}</span>
    </div>
  );
}
